import { useCallback, useEffect, useRef, useState } from 'react'

export const useImageLoaded = () => {
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState(false)
  const imgRef = useRef<HTMLImageElement | null>(null)

  const handleLoad = useCallback(() => {
    setLoaded(true)
    setError(false)
  }, [])

  const handleError = useCallback(() => {
    setLoaded(false)
    setError(true)
  }, [])

  // Use a callback ref to handle the image element
  const ref = useCallback(
    (img: HTMLImageElement | null) => {
      if (imgRef.current) {
        imgRef.current.removeEventListener('load', handleLoad)
        imgRef.current.removeEventListener('error', handleError)
      }

      imgRef.current = img

      if (img) {
        // Reset states when new image is attached
        setLoaded(false)
        setError(false)

        // Check if image is already complete (cached or loaded)
        if (img.complete) {
          if (img.naturalWidth > 0) {
            // Image loaded successfully
            handleLoad()
          } else if (img.naturalWidth === 0 && img.naturalHeight === 0) {
            // Image failed to load
            handleError()
          }
          // If complete but dimensions not yet available, wait for events
        }

        // Always add event listeners for future state changes
        img.addEventListener('load', handleLoad)
        img.addEventListener('error', handleError)
      }
    },
    [handleLoad, handleError],
  )

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (imgRef.current) {
        imgRef.current.removeEventListener('load', handleLoad)
        imgRef.current.removeEventListener('error', handleError)
      }
    }
  }, [handleLoad, handleError])

  return { ref, loaded, error }
}
